import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, Text, LoadingSpinner, Input } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useUserLogs } from '../../hooks/useUserLogs'
import { supportCenterParametersService } from '../../services/supportCenterParameters'
import type { SupportCenterParameter } from '../../types/supportCenterParameters'
import {
  SUPPORT_CENTER_PARAM_DIAS_MAX,
  SUPPORT_CENTER_PARAM_HORAS_ANTECEDENCIA,
  SUPPORT_CENTER_PARAM_TOLERANCIA_MINUTOS,
} from '../../types/supportCenterParameters'
import { MODULE_NAME, getErrorMessage } from '../../components/centralAmparo/parametrosHelpers'

function minForKey(key: string): number {
  if (key === SUPPORT_CENTER_PARAM_HORAS_ANTECEDENCIA) return 0
  if (key === SUPPORT_CENTER_PARAM_TOLERANCIA_MINUTOS) return 0
  if (key === SUPPORT_CENTER_PARAM_DIAS_MAX) return 1
  return 0
}

const ParametrosConfigTab: React.FC = () => {
  const { logUpdate } = useUserLogs()

  const [loading, setLoading] = React.useState(true)
  const [pageError, setPageError] = React.useState<string | null>(null)
  const [parameters, setParameters] = React.useState<SupportCenterParameter[]>([])

  const [editingParameter, setEditingParameter] = React.useState<SupportCenterParameter | null>(
    null
  )
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editValue, setEditValue] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    setPageError(null)
    try {
      const data = await supportCenterParametersService.listParameters()
      setParameters(data)
    } catch (error) {
      setPageError(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  const openEdit = (param: SupportCenterParameter) => {
    setEditingParameter(param)
    setEditValue(param.value)
    setFormError(null)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingParameter(null)
    setEditValue('')
    setFormError(null)
  }

  const handleSave = async () => {
    if (!editingParameter || saving) return
    const trimmed = editValue.trim()
    const min = minForKey(editingParameter.key)
    const n = Number.parseInt(trimmed, 10)
    if (!/^\d+$/.test(trimmed) || !Number.isFinite(n) || n < min) {
      setFormError(`Informe um número inteiro maior ou igual a ${min}.`)
      return
    }

    setSaving(true)
    setFormError(null)
    try {
      const oldValue = editingParameter.value
      await supportCenterParametersService.updateParameter(editingParameter.id, {
        value: String(n),
      })
      void logUpdate(
        'parameter',
        editingParameter.id,
        `Alterou parâmetro na Central de Amparo: ${editingParameter.key}`,
        { value: oldValue },
        { value: String(n) },
        { parameter_key: editingParameter.key, context: 'central_amparo' }
      )
      closeModal()
      await loadData()
    } catch (error) {
      setFormError(getErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Text size="sm" color="muted">
            Limites de agendamento da Central de Amparo. Edite o valor conforme necessário.
          </Text>
        </div>

        {pageError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {pageError}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Descrição
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Valor
                </th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {parameters.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    Nenhum parâmetro cadastrado. Execute o script SQL{' '}
                    <code className="text-xs">support_center_09_parameters.sql</code>.
                  </td>
                </tr>
              ) : (
                parameters.map((param) => (
                  <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td
                      className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-md"
                      title={param.description ?? undefined}
                    >
                      {param.description ?? '—'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {param.value}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <PermissionGuard
                        moduleName={MODULE_NAME}
                        permissionType="update"
                        fallback={null}
                      >
                        <Button variant="outline" size="sm" onClick={() => openEdit(param)}>
                          Editar
                        </Button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && editingParameter &&
        createPortal(
        <div className="fixed inset-0 z-50 overflow-x-hidden overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeModal} />
            <div
              className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Editar parâmetro
                  </Heading>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <Text size="sm" color="muted" className="mb-1">
                      Chave
                    </Text>
                    <code className="block text-sm font-mono text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/40 rounded px-3 py-2">
                      {editingParameter.key}
                    </code>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Valor
                    </label>
                    <Input
                      type="number"
                      min={minForKey(editingParameter.key)}
                      step={1}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <Text size="sm" color="muted" className="mb-1">
                      Descrição
                    </Text>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300">
                      {editingParameter.description ?? '—'}
                    </Text>
                  </div>

                  {formError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                      {formError}
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" onClick={closeModal} disabled={saving}>
                    Cancelar
                  </Button>
                  <PermissionGuard
                    moduleName={MODULE_NAME}
                    permissionType="update"
                    fallback={null}
                  >
                    <Button onClick={() => void handleSave()} disabled={saving || !editValue.trim()}>
                      {saving ? 'Salvando…' : 'Salvar'}
                    </Button>
                  </PermissionGuard>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
        )}
    </div>
  )
}

export default ParametrosConfigTab
